import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { ThreeEvent, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { useTool } from '../state/store';
import { constrainDraftPoint } from '../tools/snapping';
import { GROUND_PLANE } from '../core/scene';

// Tool hooks + previews
import { useLineTool, useCircleTool, useRectangleTool, LinePreview, CirclePreview, RectanglePreview } from '../tools/cad/DraftingTools';
import { useCurveTool, useBoxTool, CurvePreview, BoxPreview } from '../tools/nurbs/NurbsTools';
import {
    useArcTool, ArcPreview,
    usePolygonTool, PolygonPreview,
    usePolylineTool, PolylinePreview,
    useEllipseTool, EllipsePreview
} from '../tools/cad/ExtendedDraftingTools';
import { useTextTool, TextInputOverlay, useLinearDimensionTool, useRadiusDimensionTool, LinearDimensionPreview, RadialDimensionPreview } from '../tools/cad';
import { useWallTool, WallPreview, WallAlignmentGuides } from '../tools/bim/WallTool';
import {
    useDoorTool, DoorMesh,
    useWindowTool, WindowMesh,
    useFloorTool, FloorPreview,
    useColumnTool, ColumnMesh,
    useRoofTool, RoofPreview,
    useCeilingTool, CeilingPreview,
    useStairTool, StairPreview,
    useRailingTool, RailingPreview,
    useRampTool, RampPreview,
    useComponentTool, ComponentPreview,
    useAreaTool, AreaPreview,
    useToposolidTool, ToposolidPreview,
} from '../tools/bim';
import { useRevolveTool } from '../tools/nurbs';
import {
    useMoveTool, useCopyTool, useRotateTool, useScaleTool, useMirrorTool,
    useOffsetTool, usePointTool,
    MovePreview, CopyPreview, RotatePreview, MirrorPreview,
} from '../tools/cad/ModifyTools';

/**
 * Tool Scope (Phase 2, task 2.4 — lazy tool instantiation)
 *
 * Only the hook of the ACTIVE tool is mounted. Each scope owns its tool's
 * pointer handling and preview, so pointer-move storms during drawing
 * never touch the entity/BIM/overlay layers above.
 */
interface ToolScopeProps {
    children: ReactNode;
    updateSnap: (event: ThreeEvent<PointerEvent>) => void;
}

/** Module-scope scratch for the hover ground point: consumed synchronously
 *  inside constrainDraftPoint (which clones before storing) and never
 *  escapes, so one shared vector is safe. */
const hoverGroundPoint = new THREE.Vector3();

/**
 * Hover-time snap feedback. Resolves the RAW ground point through the exact
 * pipeline a click commits through (constrainDraftPoint, baseless until the
 * first point is placed) and publishes the osnap marker + statusbar readout
 * from it — the marker the user sees is by construction the point the next
 * click lands on.
 */
function useSnapUpdater(): (event: ThreeEvent<PointerEvent>) => void {
    const { camera, gl, raycaster, pointer } = useThree();
    return useCallback((event) => {
        raycaster.setFromCamera(pointer, camera);
        // Ground-plane raycast (the tools' own commit input), not the hit
        // surface: hovering a tall mesh must not skew the marker off the
        // drafting plane. Falls back to the event point on parallel rays.
        const raw = raycaster.ray.intersectPlane(GROUND_PLANE, hoverGroundPoint) ?? event.point;
        constrainDraftPoint(raw, camera, gl, null);
    }, [camera, gl, raycaster, pointer]);
}

function LineToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = useLineTool();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
        >
            {children}
            {tool.isDrawing && tool.points.length > 0 && tool.currentPoint && (
                <LinePreview from={tool.points[tool.points.length - 1]} to={tool.currentPoint} />
            )}
        </group>
    );
}

function CircleToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = useCircleTool();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
        >
            {children}
            {tool.isDrawing && tool.center && (
                <CirclePreview center={tool.center} radius={tool.radius} />
            )}
        </group>
    );
}

function RectangleToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = useRectangleTool();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
        >
            {children}
            {tool.isDrawing && tool.corner1 && tool.corner2 && (
                <RectanglePreview corner1={tool.corner1} corner2={tool.corner2} />
            )}
        </group>
    );
}

function ArcToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = useArcTool();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
        >
            {children}
            {tool.arcPreview && <ArcPreview arc={tool.arcPreview} />}
        </group>
    );
}

function PolygonToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = usePolygonTool();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
        >
            {children}
            {tool.center && (
                <PolygonPreview center={tool.center} radius={tool.previewRadius} sides={tool.sides} />
            )}
        </group>
    );
}

function PolylineToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = usePolylineTool();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
            onDoubleClick={() => { if (tool.isActive) tool.handleDoubleClick(); }}
        >
            {children}
            {tool.vertices.length > 0 && (
                <PolylinePreview vertices={tool.vertices} previewPoint={tool.previewPoint} />
            )}
            {/* Arc-mode feedback: pending midpoint marker + hint dot. */}
            {tool.arcMode && tool.pendingArcMid && (
                <mesh position={tool.pendingArcMid}>
                    <sphereGeometry args={[0.08, 8, 8]} />
                    <meshBasicMaterial color="#ff8800" />
                </mesh>
            )}
        </group>
    );
}

function EllipseToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = useEllipseTool();
    // Z-up ground plane: the major-axis angle lives in XY (same math the
    // hook uses on commit in ExtendedDraftingTools), NOT in the Z column —
    // reading axisEnd.z made radiusB collapse to 0 and the preview vanish.
    const angle = tool.center && tool.axisEnd
        ? Math.atan2(tool.axisEnd.y - tool.center.y, tool.axisEnd.x - tool.center.x)
        : 0;
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
        >
            {children}
            {tool.center && tool.axisEnd && (
                <EllipsePreview
                    center={tool.center}
                    radiusA={tool.center.distanceTo(tool.axisEnd)}
                    radiusB={tool.previewPoint ? Math.abs(
                        tool.previewPoint.clone().sub(tool.center).dot(
                            new THREE.Vector3(-Math.sin(angle), Math.cos(angle), 0)
                        )
                    ) : 0}
                    rotation={angle}
                />
            )}
        </group>
    );
}

function TextToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = useTextTool();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); }}
        >
            {children}
            {tool.isEditing && tool.editPosition && (
                <TextInputOverlay
                    position={tool.editPosition}
                    value={tool.textContent}
                    onChange={tool.setTextContent}
                    onComplete={tool.completeText}
                    multiline={tool.isMultiline}
                />
            )}
        </group>
    );
}

function DimLinearToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = useLinearDimensionTool(false);
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
        >
            {children}
            <LinearDimensionPreview point1={tool.point1} point2={tool.point2} cursor={tool.previewPos} />
        </group>
    );
}

function DimAlignedToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = useLinearDimensionTool(true);
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
        >
            {children}
            <LinearDimensionPreview point1={tool.point1} point2={tool.point2} cursor={tool.previewPos} />
        </group>
    );
}

function DimRadiusToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = useRadiusDimensionTool();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
        >
            {children}
            <RadialDimensionPreview center={tool.center} radiusPoint={tool.radiusPoint} isDiameter={tool.isDiameter} />
        </group>
    );
}

function WallToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = useWallTool();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
        >
            {children}
            <WallAlignmentGuides guides={tool.alignmentGuides} />
            {tool.isDrawing && tool.startPoint && tool.previewEnd && (
                <WallPreview start={tool.startPoint} end={tool.previewEnd} height={tool.wallHeight} thickness={tool.wallThickness} />
            )}
        </group>
    );
}

function DoorToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = useDoorTool();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
        >
            {children}
            {tool.previewPosition && (
                <DoorMesh
                    data={{
                        position: [tool.previewPosition.x, 0, tool.previewPosition.z],
                        width: tool.doorWidth,
                        height: tool.doorHeight,
                        thickness: 0.05,
                        swingAngle: 0,
                        swingDirection: tool.flipHand ? 'left' : 'right',
                        openDirection: tool.flipFacing ? 'outward' : 'inward',
                        frameWidth: 0.08,
                        material: 'wood'
                    }}
                    isSelected={false} isHovered={false} onClick={() => { }}
                />
            )}
        </group>
    );
}

function WindowToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = useWindowTool();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
        >
            {children}
            {tool.previewPosition && (
                <WindowMesh
                    data={{ position: [tool.previewPosition.x, 0, tool.previewPosition.z], width: tool.windowWidth, height: tool.windowHeight, sillHeight: tool.sillHeight, thickness: 0.1, frameWidth: 0.05, mullionCount: 1, transomCount: 0, glazingType: 'double', material: 'aluminum' }}
                    isSelected={false} isHovered={false} onClick={() => { }}
                />
            )}
        </group>
    );
}

function PolygonalToolScopeBase({
    children,
    updateSnap,
    useHook,
    renderPreview,
    hasDoubleClick = false,
}: ToolScopeProps & {
    useHook: () => any;
    renderPreview: (tool: any) => ReactNode;
    hasDoubleClick?: boolean;
}) {
    const tool = useHook();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
            onDoubleClick={() => { if (hasDoubleClick && tool.isActive) tool.handleDoubleClick(); }}
        >
            {children}
            {renderPreview(tool)}
        </group>
    );
}

function FloorToolScope(props: ToolScopeProps) {
    return (
        <PolygonalToolScopeBase
            {...props}
            useHook={useFloorTool}
            renderPreview={(tool) => tool.vertices.length > 0 && (
                <FloorPreview vertices={tool.vertices} previewPoint={tool.previewPoint} />
            )}
            hasDoubleClick
        />
    );
}

function RoofToolScope(props: ToolScopeProps) {
    return (
        <PolygonalToolScopeBase
            {...props}
            useHook={useRoofTool}
            renderPreview={(tool) => tool.vertices.length > 0 && (
                <RoofPreview vertices={tool.vertices} previewPoint={tool.previewPoint} />
            )}
            hasDoubleClick
        />
    );
}

function CeilingToolScope(props: ToolScopeProps) {
    return (
        <PolygonalToolScopeBase
            {...props}
            useHook={useCeilingTool}
            renderPreview={(tool) => tool.vertices.length > 0 && (
                <CeilingPreview vertices={tool.vertices} previewPoint={tool.previewPoint} />
            )}
            hasDoubleClick
        />
    );
}

function RailingToolScope(props: ToolScopeProps) {
    return (
        <PolygonalToolScopeBase
            {...props}
            useHook={useRailingTool}
            renderPreview={(tool) => tool.points.length > 0 && (
                <RailingPreview points={tool.points} previewPoint={tool.previewPoint} />
            )}
            hasDoubleClick
        />
    );
}

function ToposolidToolScope(props: ToolScopeProps) {
    return (
        <PolygonalToolScopeBase
            {...props}
            useHook={useToposolidTool}
            renderPreview={(tool) => tool.points.length > 0 && (
                <ToposolidPreview points={tool.points} previewPoint={tool.previewPoint} />
            )}
            hasDoubleClick
        />
    );
}

function StairToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = useStairTool();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
        >
            {children}
            {tool.startPoint && tool.previewEndPoint && (
                <StairPreview
                    startPoint={tool.startPoint}
                    endPoint={tool.previewEndPoint}
                    width={tool.width}
                    treadDepth={tool.treadDepth}
                    riserHeight={tool.riserHeight}
                />
            )}
        </group>
    );
}

function RampToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = useRampTool();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
        >
            {children}
            {tool.startPoint && tool.previewEndPoint && (
                <RampPreview
                    startPoint={tool.startPoint}
                    endPoint={tool.previewEndPoint}
                    width={tool.width}
                    slope={tool.slope}
                />
            )}
        </group>
    );
}

function ComponentToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = useComponentTool();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
        >
            {children}
            {tool.previewPosition && (
                <ComponentPreview position={tool.previewPosition} rotation={tool.rotation} />
            )}
        </group>
    );
}

function AreaToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = useAreaTool();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
        >
            {children}
            {tool.previewPosition && <AreaPreview position={tool.previewPosition} />}
        </group>
    );
}

/**
 * CAD column scope: the lowercase 'column' tool id was wired to a hook
 * (useColumnTool) that was imported but never mounted, so typing COLUMN in
 * the CAD command line activated nothing.
 */
function ColumnDraftToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = useColumnTool();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
        >
            {children}
            {tool.previewPosition && (
                <ColumnMesh
                    data={{
                        position: [tool.previewPosition.x, tool.previewPosition.y, tool.columnHeight / 2],
                        width: tool.columnWidth,
                        depth: tool.columnDepth,
                        height: tool.columnHeight,
                        baseLevel: 0,
                        topLevel: tool.columnHeight,
                        shape: tool.shape,
                        material: 'concrete',
                    }}
                    isSelected={false}
                    isHovered={false}
                    onClick={() => { }}
                />
            )}
        </group>
    );
}

function CurveToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = useCurveTool();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
        >
            {children}
            {tool.controlPoints.length > 0 && (
                <CurvePreview controlPoints={tool.controlPoints} currentPoint={tool.currentPoint} degree={tool.degree} />
            )}
        </group>
    );
}

function BoxToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = useBoxTool();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
        >
            {children}
            {tool.isDrawing && tool.corner1 && tool.corner2 && (
                <BoxPreview corner1={tool.corner1} corner2={tool.corner2} height={tool.height} />
            )}
        </group>
    );
}

function RevolveToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = useRevolveTool();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
        >
            {children}
        </group>
    );
}

function MoveToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = useMoveTool();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
        >
            {children}
            <MovePreview base={tool.base} current={tool.preview} />
        </group>
    );
}

function CopyToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = useCopyTool();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
        >
            {children}
            <CopyPreview base={tool.base} current={tool.preview} color="#00ffff" />
        </group>
    );
}

function RotateToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = useRotateTool();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
        >
            {children}
            <RotatePreview center={tool.center} refPoint={tool.ref} previewPoint={tool.preview} />
        </group>
    );
}

function ScaleToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = useScaleTool();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
        >
            {children}
            <RotatePreview center={tool.base} refPoint={tool.ref} previewPoint={tool.preview} />
        </group>
    );
}

function MirrorToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = useMirrorTool();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); if (tool.isActive) tool.handlePointerMove(); }}
        >
            {children}
            <MirrorPreview p1={tool.p1} p2={tool.preview} />
        </group>
    );
}

function OffsetToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = useOffsetTool();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); }}
        >
            {children}
        </group>
    );
}

function PointToolScope({ children, updateSnap }: ToolScopeProps) {
    const tool = usePointTool();
    return (
        <group
            onPointerDown={(e) => { if (e.button === 0 && tool.isActive) tool.handlePointerDown(e); }}
            onPointerMove={(e) => { updateSnap(e); }}
        >
            {children}
        </group>
    );
}

/**
 * Active-tool switch. Only the matching scope mounts; every other tool's
 * hook stays un-instantiated (lazy per-tool scopes, task 2.4).
 *
 * `exclude` lets a host viewport hand specific tool ids back to its own
 * controller: the BIM viewport mounts ToolScope to gain the CAD drafting
 * tools it lacks, but its class-based controller (useToolController) keeps
 * owning MOVE/COPY/ROTATE/... and the BIM categories — mounting the CAD
 * scope for those would double-handle the same gesture (e.g. a wall moved
 * once by the joint-aware BIM class and again by the CAD data-bag path).
 * The ownership sets live in core/toolsOwnership.
 */
export { BIM_OWNED_TOOL_IDS } from '../core/toolsOwnership';

function ActiveToolScope({ tool, children, updateSnap, exclude }: ToolScopeProps & { tool: string; exclude?: ReadonlySet<string> }) {
    if (exclude?.has(tool)) return <>{children}</>;
    const scopeProps = { children, updateSnap };
    switch (tool) {
        case 'line': return <LineToolScope {...scopeProps} />;
        case 'circle': return <CircleToolScope {...scopeProps} />;
        case 'rectangle': return <RectangleToolScope {...scopeProps} />;
        case 'arc': return <ArcToolScope {...scopeProps} />;
        case 'polygon': return <PolygonToolScope {...scopeProps} />;
        case 'polyline': return <PolylineToolScope {...scopeProps} />;
        case 'ellipse': return <EllipseToolScope {...scopeProps} />;
        case 'text': case 'mtext': return <TextToolScope {...scopeProps} />;
        case 'dimLinear': return <DimLinearToolScope {...scopeProps} />;
        case 'dimAligned': return <DimAlignedToolScope {...scopeProps} />;
        case 'dimRadius': case 'dimDiameter': return <DimRadiusToolScope {...scopeProps} />;
        case 'wall': return <WallToolScope {...scopeProps} />;
        case 'door': return <DoorToolScope {...scopeProps} />;
        case 'window': return <WindowToolScope {...scopeProps} />;
        case 'floor': return <FloorToolScope {...scopeProps} />;
        case 'roof': return <RoofToolScope {...scopeProps} />;
        case 'ceiling': return <CeilingToolScope {...scopeProps} />;
        case 'stair': return <StairToolScope {...scopeProps} />;
        case 'railing': return <RailingToolScope {...scopeProps} />;
        case 'ramp': return <RampToolScope {...scopeProps} />;
        case 'component': return <ComponentToolScope {...scopeProps} />;
        case 'area': return <AreaToolScope {...scopeProps} />;
        case 'column': return <ColumnDraftToolScope {...scopeProps} />;
        case 'toposolid': return <ToposolidToolScope {...scopeProps} />;
        case 'curve': return <CurveToolScope {...scopeProps} />;
        case 'box': return <BoxToolScope {...scopeProps} />;
        case 'revolve': return <RevolveToolScope {...scopeProps} />;
        case 'MOVE': case 'move': return <MoveToolScope {...scopeProps} />;
        case 'COPY': case 'copy': return <CopyToolScope {...scopeProps} />;
        case 'ROTATE': case 'rotate': return <RotateToolScope {...scopeProps} />;
        case 'SCALE': case 'scale': return <ScaleToolScope {...scopeProps} />;
        case 'MIRROR_PICK': case 'mirror': return <MirrorToolScope {...scopeProps} />;
        case 'OFFSET': case 'offset': return <OffsetToolScope {...scopeProps} />;
        case 'point': case 'points': return <PointToolScope {...scopeProps} />;
        default:
            // No drafting scope owns the pointer (select tool and friends):
            // still track the cursor through the shared pipeline so the osnap
            // marker and the statusbar readout stay live while idle, 2D
            // style. Scoped tools publish through their own handlers instead.
            return (
                <group onPointerMove={(e) => updateSnap(e)}>
                    {children}
                </group>
            );
    }
}

export function ToolScope({ children, exclude }: { children: ReactNode; exclude?: ReadonlySet<string> }) {
    const tool = useTool();
    const updateSnap = useSnapUpdater();

    return (
        <ActiveToolScope tool={tool} updateSnap={updateSnap} exclude={exclude}>
            {children}
        </ActiveToolScope>
    );
}






